import { Typography, FormHelperText, Box, Link, makeStyles, createStyles, Snackbar } from '@material-ui/core';
import * as React from 'react';
import cn from 'classnames';
import Collapse from '../../components/Collapse/Collapse';
import Loader from '../../assets/icons/Loader';
import Btn from '../../components/Btn/Btn';
import FormikCheck from '../../components/FormikInputWrappers/FormikCheck';
import FormikFormattedTextField from '../../components/FormikInputWrappers/FormikFormattedTextField';
import { TwilioPhoneVerificationType, IdentityConfirmationOption } from '../../generated/graphql';
import { pluralize } from '../../utils/pluralize';
import { useIdentificationConfirmationPhoneCode } from './utils/useIdentificationConfirmationPhoneCode';
import { TranquiTheme } from '../App/theme';
import useDictionary from '../../dictionaries/context/useDictionary';
import RenderText from '../../dictionaries/RenderText';
import { SendIcon } from '../../assets/icons/SendIcon';
import Alert from '../../components/Alert/Alert';
import { IdentificationConfirmationStyles } from './IdentificationConfirmationStyles';
import { requiredPoliciesAreAccepted } from './utils/requiredPoliciesAreAccepted';
import { useLegalPolicyAccepted } from './utils/useLegalAcceptance';
import ValidationAttemptsCounter from './components/ValidationAttemptsCounter';

type IdentificationConfirmationPhoneCodeVerificationProps =
ReturnType<typeof useIdentificationConfirmationPhoneCode> & {
  baseClasses: IdentificationConfirmationStyles;
  option: IdentityConfirmationOption;
};

const DEFAULT_PHONE_VER_TYPE = TwilioPhoneVerificationType.Sms;

const IdentificationConfirmationPhoneCodeVerification:
React.FC<IdentificationConfirmationPhoneCodeVerificationProps> = (props) => {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const classes = useStyles();
  const { gT } = useDictionary();
  const {
    formik, type, newVerError, isActive, remaining,
    sendCode, baseClasses, option,
  } = props;
  const policyUpToDate = useLegalPolicyAccepted();

  React.useEffect(() => {
    sendCode(DEFAULT_PHONE_VER_TYPE);
  }, [ sendCode ]);

  const [ open, setOpen ] = React.useState(true);
  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const handleSend = () => sendCode(DEFAULT_PHONE_VER_TYPE);
  const disableSubmit = !formik.isValid
    || !requiredPoliciesAreAccepted(formik.values, policyUpToDate);

  return (
    <form onSubmit={formik.handleSubmit}>
      {newVerError && (
      <Snackbar
        open={newVerError && open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        className="mt-7"
      >
        <Alert title={gT('validation_email_error_title')} color="error" icon="info" iconColor="error" className={baseClasses.alert}>
          {gT('validation_email_error_description')}
        </Alert>
      </Snackbar>
      )}
      <FormikFormattedTextField
        label="Ingresa el código de verificación"
        valueId="code"
        data-test="cy-codeValidation-input"
        formik={formik}
        placeholder="123 456"
        disableAutoComplete
        fullWidth
        format={{
          format: '### ###',
        }}
      />
      {newVerError && (
        <FormHelperText error className={cn('mt-1', baseClasses.textError)}>
          Hubo un error al enviar el código, revisa el número
          {type === TwilioPhoneVerificationType.WhatsApp
            ? ' o intenta con un mensaje de texto.'
            : ' e intenta nuevamente.'}
        </FormHelperText>
      )}

      <ValidationAttemptsCounter
        remainingAttempts={option?.remainingAttempts}
      />

      <Box className="mt-3 mb-4">
        <Box style={{ width: '100%' }} display="flex" justifyContent="space-between" alignItems="flex-end">
          <Box flexGrow={2}>
            <Collapse in={!isActive}>
              <Btn className={classes.customTextButton} variant="text" onClick={handleSend} data-test="cy-resend-code-btn">
                {gT('validation_phone_msgOption_resend_button')}
                <SendIcon className="btnIcon" size={15} />
              </Btn>
            </Collapse>
            <Collapse in={isActive}>
              <Box style={{ width: '100%' }} display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                <Loader className="mb-1" />
                <Typography className={classes.timerText}>
                  Debes esperar
                  {' '}
                  {remaining}
                  {' '}
                  segundo
                  {pluralize(remaining)}
                  {' '}
                  para enviar un nuevo código
                </Typography>
              </Box>
            </Collapse>
          </Box>
          <Btn
            style={{ flexShrink: 0 }}
            color="primary"
            disabled={disableSubmit}
            type="submit"
            data-test="cy-confirmValidationPhone-btn"
            className="ml-1"
          >
            <RenderText textKey="general_no_auth_button" />
          </Btn>
        </Box>
      </Box>

      {!policyUpToDate ? (
        <>
          <FormikCheck
            className={cn('mt-3 mb-1')}
            labelCheck={(
              <Box className={classes.textCheckBox}>
                Acepto&nbsp;
                <Link href="https://tranquifinanzas.com/politica-privacidad/" target="_blank">Política de tratamiento de datos</Link>
                ,&nbsp;
                <Link href="https://tranquifinanzas.com/terminos-y-condiciones-de-uso/" target="_blank">Términos y condiciones</Link>
              &nbsp;y&nbsp;
                <Link href="https://tranquifinanzas.com/aviso-de-privacidad/" target="_blank">Aviso de privacidad</Link>
              </Box>
          )}
            valueId="policiesAndTermsA"
            formik={formik}
          />
          <FormikCheck
            className="mt-1 mb-1"
            labelCheck={(
              <Box className={classes.textCheckBox}>
                He leído y autorizo el&nbsp;
                <Link href="https://tranquifinanzas.com/autorizacion-para-el-tratamiento-de-datos-personales/" target="_blank">Tratamiento de datos.</Link>
              </Box>
          )}
            valueId="policiesAndTermsB"
            formik={formik}
          />
        </>
      ) : null}
    </form>
  );
};

const useStyles = makeStyles((theme: TranquiTheme) => createStyles({
  textCheckBox: {
    ...theme.typography.body2,
    color: theme.palette.text2.main,
    textAlign: 'left',
  },
  customTextButton: {
    color: theme.palette.lavender.main,
    fontWeight: 600,
    marginRight: 'auto',
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '& .btnIcon': {
      marginLeft: theme.spacing(1),
    },
  },
  timerText: {
    ...theme.typography.small as any,
    fontWeight: 600,
    color: theme.palette.secondary.main,
  },
  btnWrapper: {
    display: 'flex',
    flexDirection: 'column-reverse',
    justifyContent: 'center',
    alignItems: 'center',
    rowGap: `${theme.spacing(1)}px`,
  },
  btnWrapperNormal: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

export default IdentificationConfirmationPhoneCodeVerification;
